import type { GetStaticProps, NextPage } from "next";
import Image from "next/legacy/image";
import Link from "next/link";
import SubpageShell from "components/common/SubpageShell";
import {
  getBlogPostPreviews,
  getBlogSettings,
  type BlogPostPreview,
  type BlogSettings
} from "lib/cms/blog";
import {
  CMS_REVALIDATE_SECONDS,
  serverSideCmsTranslations
} from "lib/cms/translations";
import styles from "../../styles/blog/Blog.module.css";

interface BlogPageProps {
  posts: BlogPostPreview[];
  settings: BlogSettings;
  locale: string;
}

const formatDate = (date: string, locale: string) =>
  new Intl.DateTimeFormat(locale === "en" ? "en-US" : "de-DE", {
    day: "2-digit",
    month: "long",
    year: "numeric"
  }).format(new Date(date));

export const getStaticProps: GetStaticProps<BlogPageProps> = async ({
  locale
}) => {
  const pageLocale = locale ?? "de";

  return {
    props: {
      locale: pageLocale,
      posts: await getBlogPostPreviews(pageLocale),
      settings: await getBlogSettings(pageLocale),
      ...(await serverSideCmsTranslations(pageLocale, ["common"]))
    },
    revalidate: CMS_REVALIDATE_SECONDS
  };
};

const BlogPage: NextPage<BlogPageProps> = ({ posts, settings, locale }) => (
  <SubpageShell
    title={settings.websiteTitle}
    description={settings.websiteDescription}
    path="/blog"
    eyebrow={settings.eyebrow}
    heading={settings.heading}
    subtitle={settings.subtitle}
    doAnimation
  >
    <div className={styles.container}>
      {
        posts.length === 0 ?
          <div className={styles.emptyState}>
            <h2 className={styles.emptyTitle}>{settings.emptyTitle}</h2>
            <p className={styles.emptyText}>{settings.emptyText}</p>
          </div> :
          <div className={styles.postGrid}>
            {posts.map((post) =>
              post.heroImagePath ? (
                <Link
                  key={post.slug}
                  href={`/blog/${post.slug}`}
                  className={styles.postCardCover}
                  aria-label={post.title}
                >
                  <div className={styles.postCoverFrame}>
                    <Image
                      src={post.heroImagePath}
                      layout="fill"
                      objectFit="cover"
                      sizes="(max-width: 900px) 100vw, 1280px"
                      alt={post.heroImageAlt ?? post.title}
                    />
                  </div>
                </Link>
              ) : (
                <Link key={post.slug} href={`/blog/${post.slug}`} className={styles.postCard}>
                  <div className={styles.postBody}>
                    <div className={styles.postMeta}>
                      {post.category && <span className={styles.postCategory}>{post.category}</span>}
                      <span>{formatDate(post.publishedAt, locale)}</span>
                      {post.readingTime && <span>{post.readingTime}</span>}
                    </div>
                    <h2 className={styles.postTitle}>{post.title}</h2>
                    <p className={styles.postExcerpt}>{post.excerpt}</p>
                    <span className={styles.postLink}>
                      {settings.readMoreLabel} <span aria-hidden="true">→</span>
                    </span>
                  </div>
                </Link>
              )
            )}
          </div>
      }
    </div>
  </SubpageShell>
);

export default BlogPage;
